import React, { CSSProperties, Ref, SyntheticEvent } from 'react';
import { Grid, GridImperativeAPI } from 'react-window';
import { Box, Flex } from '@chakra-ui/react';
import { CampaignMapping } from 'src/api/campaignMappings';

type CampaignMappingBodyProps = {
  cardBg: string;
  rows: CampaignMapping[];
  columns: string[];
  renderCampaignCell: (
    row: CampaignMapping,
    rowIndex: number,
  ) => React.ReactNode;
  renderPlatformCell: (row: CampaignMapping, column: string) => React.ReactNode;
  leftGridRef: React.RefObject<unknown>;
  rightGridRef: React.RefObject<unknown>;
  syncFromLeft: (p: SyntheticEvent) => void;
  syncFromRight: (p: SyntheticEvent) => void;
  rowHeight: () => number;
  rowCount: number;
  campaignNameColWidth: number;
  platformColWidth: number;
};

export const CampaignMappingBody: React.FC<CampaignMappingBodyProps> = ({
  cardBg,
  rows,
  columns,
  renderCampaignCell,
  renderPlatformCell,
  leftGridRef,
  rightGridRef,
  syncFromLeft,
  syncFromRight,
  rowHeight,
  rowCount,
  campaignNameColWidth,
  platformColWidth,
}) => {
  const LeftCell = ({
    rowIndex,
    style,
  }: {
    ariaAttributes: { 'aria-colindex': number; role: 'gridcell' };
    columnIndex: number;
    rowIndex: number;
    style: CSSProperties;
  }) => {
    const row = rows[rowIndex];
    if (!row) return null;
    return (
      <Box
        style={style}
        bg={cardBg}
        borderBottom="1px solid"
        borderColor="border.layout">
        {renderCampaignCell(row, rowIndex)}
      </Box>
    );
  };

  const RightCell = ({
    rowIndex,
    columnIndex,
    style,
  }: {
    ariaAttributes: { 'aria-colindex': number; role: 'gridcell' };
    columnIndex: number;
    rowIndex: number;
    style: CSSProperties;
  }) => {
    const row = rows[rowIndex];
    if (!row) return null;
    const column = columns[columnIndex];
    return (
      <Box
        style={style}
        bg={cardBg}
        borderBottom="1px solid"
        borderColor="border.layout">
        {renderPlatformCell(row, column)}
      </Box>
    );
  };

  return (
    <Box>
      <Flex>
        <Box
          flex="0 0 auto"
          w={`${campaignNameColWidth}px`}
          borderRight="1px solid"
          borderColor="border.layout">
          <Grid
            gridRef={leftGridRef as Ref<GridImperativeAPI>}
            columnCount={1}
            columnWidth={campaignNameColWidth}
            rowCount={rowCount}
            rowHeight={rowHeight}
            cellComponent={LeftCell}
            cellProps={{}}
            onScroll={syncFromLeft}
            style={{
              height: 536,
              width: campaignNameColWidth,
              overflowY: 'auto',
              overflowX: 'hidden',
              scrollbarWidth: 'none',
            }}
          />
        </Box>

        <Box flex="1 1 auto" overflowX="auto">
          <Grid
            gridRef={rightGridRef as Ref<GridImperativeAPI>}
            columnCount={columns.length}
            columnWidth={platformColWidth}
            rowCount={rowCount}
            rowHeight={rowHeight}
            cellComponent={RightCell}
            onScroll={syncFromRight}
            cellProps={{}}
            style={{
              height: 536,
              overflowX: 'auto',
              overflowY: 'scroll',
              scrollbarWidth: 'thin',
              scrollbarColor: '#c1c1c1 #f1f1f1',
            }}
          />
        </Box>
      </Flex>
    </Box>
  );
};
